import { Button as BaseButton } from '@base-ui/react/button';
import type { ComponentProps, ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import { cn } from '../lib/cn.ts';

export type RailState = 'expanded' | 'compact';

/*
 * The frame every screen in the product sits in: a floating rail card beside a
 * scrolling main column.
 *
 * `Root` owns the only piece of state — whether the rail is expanded — and
 * publishes it as `data-rail` on a named group. Every part below reads it
 * through `group-data-[rail=compact]/shell:`, so collapsing the rail is one
 * attribute and no context, no prop drilling, and no second render path that
 * can drift from the first.
 */

/**
 * Below this, the rail is compact whatever the caller asked for, and the second
 * level of navigation goes away entirely. Measured on the product: expanded at
 * 1000px, compact at 992.
 */
export const RAIL_BREAKPOINT = 1000;

/**
 * Whether there is room for an expanded rail. `true` on the server and before
 * hydration, because the wide layout is the one the markup is written for and
 * a flash from compact to expanded is worse than the reverse.
 */
export function useRoomForRail(): boolean {
  return useSyncExternalStore(subscribe, hasRoom, () => true);
}

const QUERY = `(min-width: ${RAIL_BREAKPOINT}px)`;

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const media = window.matchMedia(QUERY);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function hasRoom(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia(QUERY).matches;
}

export interface AppShellRootProps extends ComponentProps<'div'> {
  /**
   * What the rail does **when there is room**. Below `RAIL_BREAKPOINT` it is
   * compact regardless — the product does the same, and a 256px rail on a
   * 640px screen spends 40% of the page on navigation.
   */
  rail?: RailState;
}

function Root({ rail = 'expanded', className, ...props }: AppShellRootProps) {
  const room = useRoomForRail();

  return (
    <div
      data-rail={room ? rail : 'compact'}
      className={cn(
        'group/shell relative grid h-dvh overflow-hidden bg-surface text-md text-ink',
        'grid-rows-[100dvh] grid-cols-[256px_minmax(0,1fr)]',
        'data-[rail=compact]:grid-cols-[104px_minmax(0,1fr)]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The rail is a card that floats inside the grid column rather than a panel
 * welded to the edge — the 16px of page showing around it is what makes the
 * layout read as this product's. The shadow is not optional either: without it
 * the card reads as a region of the page rather than as something laid on it.
 */
function Rail({ className, ...props }: ComponentProps<'aside'>) {
  return (
    <aside
      className={cn(
        // No horizontal padding in either state: the rail is a bare card, and
        // the 8px its rows sit in from the edge belongs to the rows.
        'flex h-[calc(100dvh-32px)] min-w-0 flex-col gap-0.5 self-start',
        'mx-4 my-4 rounded-card bg-surface-raised px-0 py-3 shadow-panel',
        'group-data-[rail=compact]/shell:w-20',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The top row of the rail: the wordmark, and whatever control sits opposite it
 * — in this product, the collapse toggle. One row, not two stacked, which is
 * what keeps the rail's head the same height as a nav item's.
 */
function Brand({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'mb-2 flex h-10 flex-none items-center justify-between gap-2 px-4',
        'font-display font-bold tracking-tight text-ink',
        'group-data-[rail=compact]/shell:justify-center group-data-[rail=compact]/shell:px-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * The square that collapses the rail, and the pattern for any rail-top control.
 * Positioned by whatever holds it — usually `Brand`.
 */
function RailToggle({ className, ...props }: ComponentProps<'button'>) {
  return (
    <button
      type="button"
      className={cn(
        'flex size-10 flex-none cursor-pointer items-center justify-center',
        'rounded-sm border border-transparent text-ink-faint [&>svg]:size-[19px]',
        'transition-colors duration-150 ease-out-quint',
        'hover:bg-brand-100/38 hover:text-ink',
        'data-pressed:bg-brand-200 data-pressed:text-brand-600',
        className,
      )}
      {...props}
    />
  );
}

function Nav({ className, ...props }: ComponentProps<'nav'>) {
  // 8px between rows, in both states: a 48px row on a 56px pitch.
  return <nav className={cn('flex min-w-0 flex-col gap-2 px-1', className)} {...props} />;
}

/** The uppercase mono divider between groups of destinations. */
function NavGroupLabel({ className, ...props }: ComponentProps<'p'>) {
  return (
    <p
      className={cn(
        'px-2.5 pt-4 pb-1.5 text-3xs font-bold tracking-[0.11em] text-ink-faint uppercase',
        'group-data-[rail=compact]/shell:hidden',
        className,
      )}
      {...props}
    />
  );
}

export interface NavItemProps extends ComponentProps<typeof BaseButton> {
  active?: boolean;
  icon?: ReactNode;
}

/**
 * A destination: a 48px **pill** on a 56px pitch, its label 16/22.4, and the
 * current one marked by a coral fill *and* by weight 600.
 *
 * Every number here is measured — `prd/measured/bridge-2026-09-25.md` — and it
 * took three passes to get them, which is the part worth keeping.
 *
 * #88 read the rail as a 56px rectangle at radius 8 with a 20px icon, and was
 * wrong about all three. #99 reverted that pass **whole**, restored the pill
 * correctly, and in doing so put back two things #88 had got right: the card's
 * 16px margins and the active label's weight. The size of the label, 16 rather
 * than the 14 carried since April, nobody had ever checked at all.
 *
 * So the rule «one reading against two agreeing ones loses» is the wrong
 * lesson, and it was mine. A mixed pass cannot be accepted whole or rejected
 * whole — it has to be measured again, field by field. Two sources agreeing
 * is not evidence; it is two people having read the same screenshot.
 *
 * Built on Base UI's button, so `render` turns it into whatever a router hands
 * it: `<AppShell.NavItem render={<Link to="/brands" />}>` is an anchor with a
 * real href — which a destination must be, or the middle click and the "open
 * in new tab" a rail invites both die. Compact turns it into a 64x56 tile and
 * drops the label, which is why the label has to be a child element rather than
 * text.
 */
function NavItem({ active, icon, className, children, ...props }: NavItemProps) {
  return (
    <BaseButton
      aria-current={active ? 'page' : undefined}
      className={cn(
        // 999 rather than `rounded-full`: the same circle to the eye, but
        // Tailwind's full is `calc(infinity * 1px)` and a gate comparing
        // computed styles cannot read that as a length (`prd/measured/funnel.json`).
        'group/nav-item flex min-h-12 min-w-0 cursor-pointer items-center gap-3 rounded-[999px] px-5 py-2',
        // 16px/22.4px, measured through the bridge on 2026-09-25. It was 14/21
        // from April until then — a number nobody had ever checked, carried
        // through two passes that argued about everything except this.
        'text-left text-[16px] leading-[22.4px] text-ink',
        'transition-colors duration-150 ease-out-quint',
        // Hover is the active pill's own colour, faded — not a neutral grey.
        active ? 'bg-brand-200 font-semibold' : 'hover:bg-brand-100/38',
        // A destination that is not there yet has to look it. Quiet text, no
        // hover, and the cursor says so before the click does — the product
        // has no disabled rail item, so this one is ours and deliberate.
        'disabled:cursor-not-allowed disabled:text-ink-faint disabled:hover:bg-transparent',
        // Compact is a 64x48 pill, not a rounded square: the marker changes
        // shape with the rail, it does not merely shrink.
        'group-data-[rail=compact]/shell:mx-auto group-data-[rail=compact]/shell:h-12',
        'group-data-[rail=compact]/shell:w-16 group-data-[rail=compact]/shell:justify-center',
        'group-data-[rail=compact]/shell:p-0',
        className,
      )}
      {...props}
    >
      {icon ? (
        <span
          // The one measurement marker in this package: the icon's box belongs
          // to the component rather than to any screen, so the gate has to find
          // it here or not at all.
          data-fidelity="nav-item-icon"
          className={cn(
            'flex size-6 flex-none items-center justify-center',
            active ? 'text-ink' : 'text-ink-strong',
            'group-disabled/nav-item:text-ink-faint',
          )}
        >
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate group-data-[rail=compact]/shell:hidden">{children}</span>
    </BaseButton>
  );
}

/** Pushes what follows it to the bottom of the rail — the account row. */
function RailSpacer(props: ComponentProps<'div'>) {
  return <div className="mt-auto" {...props} />;
}

/**
 * A quiet full-width row for the rail's foot: the signed-in account, a support
 * link. Lighter than a nav item on purpose — it is not a destination.
 */
function RailRow({ className, ...props }: ComponentProps<typeof BaseButton>) {
  return (
    <BaseButton
      className={cn(
        // The same pill a nav item is, one shade quieter on hover.
        'mx-2 flex min-h-12 min-w-0 cursor-pointer items-center gap-3 rounded-[999px] px-5 py-2',
        'text-left text-bodysm text-ink-muted',
        'transition-colors duration-150 ease-out-quint hover:bg-brand-100/32 hover:text-ink',
        'group-data-[rail=compact]/shell:justify-center group-data-[rail=compact]/shell:px-0',
        className,
      )}
      {...props}
    />
  );
}

function Main({ className, ...props }: ComponentProps<'main'>) {
  return (
    <main className={cn('flex min-h-0 min-w-0 flex-col overflow-hidden', className)} {...props} />
  );
}

/**
 * The only thing that scrolls. Keeping the scroll here rather than on the page
 * is what lets the rail stay put and a page header stay pinned above it.
 */
function Scroll({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('min-h-0 min-w-0 flex-1 overflow-auto', className)} {...props} />;
}

export const AppShell = {
  Root,
  Rail,
  Brand,
  RailToggle,
  Nav,
  NavGroupLabel,
  NavItem,
  RailSpacer,
  RailRow,
  Main,
  Scroll,
};
